import { Template } from "@xapp/ovai-lib";
import { setRecentlyVisitedOrgs } from "components/RecentlyVisitedOrgs";
import { Organization, useGetAppStatusTotalsForOrgQuery, useGetAppsForOrgQuery, useUpdateStatusMutation, OrgAppQuerySortByParametersOrder, SortableAppAttributes } from "generated/graphql";
import { ClientApp } from "models";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { createApp, uploadTemplate } from "routes/App/modules";
import { useQueryState } from "utils/useQuerySearch";
import { useProfile } from "utils/useProfile";
import { AppTableProps, OrgOverviewView } from "../components/OrgOverviewView";
import { AppsTableData, AppStatus } from "../Types";
import { getAppsTableData } from "../utils";
import { isAppStatus } from "../guards";
import { useWrapUrql } from "utils/graphql/useWrapUrql";
import { Redirect, useHistory } from "react-router";

/**
 * Translates the status to one used for filtering that contains some subtle variations.
 */
const STATUS_TO_FILTERS: { [status: string]: string[] } = {
    "Live": ["Live", "Live: Requires Attention", "Live: Requires Cert"],
    "Submitted for Review": ["Submitted For Review", "Submitted for Review"],
    "Ready for Install": ["Ready for Install", "Ready For Install"],
    "Requires Attention": ["Requires Attention"],
    "In Review": ["In Review", "Under Review"],
    "Cancelled": ["Cancelled"],
    "Incomplete": ["Incomplete"]
}

export interface OrgOverviewViewContainerProps {
    readonly organization: Organization;
    readonly organizationId: string;
    readonly managementOnly?: boolean;
}

export enum OrderByType { DATE_DESC = "DATE_DESC", DATE_ASC = "DATE_ASC", NAME_DESC = "NAME_DESC", NAME_ASC = "NAME_ASC" }

const orderMap = {
    DATE_DESC: {
        order: OrgAppQuerySortByParametersOrder.Desc,
        sortBy: SortableAppAttributes.StatusTime
    },
    DATE_ASC: {
        order: OrgAppQuerySortByParametersOrder.Asc,
        sortBy: SortableAppAttributes.StatusTime
    },
    NAME_DESC: {
        order: OrgAppQuerySortByParametersOrder.Desc,
        sortBy: SortableAppAttributes.AppId
    },
    NAME_ASC: {
        order: OrgAppQuerySortByParametersOrder.Asc,
        sortBy: SortableAppAttributes.AppId
    },
}

export const OrgOverviewViewContainer: FC<OrgOverviewViewContainerProps> = (props) => {
    const { organization, organizationId, managementOnly } = props;

    const [statusQueryRaw] = useQueryState("status");

    // Commenting out until we either remove or use again with more analytics features
    // const now = useMemo(() => moment(), []);
    // const [start, setStart] = useState<Date>(now.clone().add(-1, "week").toDate());
    // const [end, setEnd] = useState<Date>(now.toDate());
    const [selectedStatus, setSelectedStatus] = useState<AppStatus>();
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState(10);
    const [orderBy, setOrderBy] = useState<OrderByType>(OrderByType.DATE_ASC);

    const [appStatusTotals] = useGetAppStatusTotalsForOrgQuery({
        requestPolicy: "cache-and-network",
        variables: {
            organizationId
        }
    });

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
    }

    const from = useMemo(() => {
        return (currentPage - 1) * pageSize
    }, [currentPage, pageSize])

    const [appsByStats] = useGetAppsForOrgQuery({
        requestPolicy: "cache-and-network",
        variables: {
            organizationId,
            byStatusType: STATUS_TO_FILTERS[selectedStatus],
            from,
            size: pageSize,
            nuSortBy: orderMap[orderBy]
        }
    });

    const updateApplicationStatus = useWrapUrql(useUpdateStatusMutation(), {
        onSuccessMessage: "Application status updated",
    });

    const user = useProfile();

    useEffect(() => {
        if (isAppStatus(statusQueryRaw)) {
            setSelectedStatus(statusQueryRaw);
        }
    }, [statusQueryRaw])

    useEffect(() => {
        //push the org data to localStorage
        setRecentlyVisitedOrgs({
            organizationId: organization.organizationId,
            name: organization.name,
            logoUrl: organization.logoUrl,
        });
    }, [organization]);

    const dispatch = useDispatch();

    // TODO: Swap these out with our normal modal for app creation
    // Replace with a solution that works here and in OrgViewContainer
    const handleCreateApp = useCallback(
        (app: ClientApp) => {
            dispatch(createApp(app));
        },
        [dispatch]
    );

    const handleUploadTemplate = useCallback(
        (template: Template) => {
            dispatch(uploadTemplate(template, true));
        },
        [dispatch]
    );

    const handleSelectPage = useCallback((page: number) => {
        setCurrentPage(page)
    }, []);

    const totals = useMemo(() => {

        const total = appStatusTotals?.data?.org?.allApps?.total ?? 0;

        const live = appStatusTotals.data?.org?.live?.total ?? 0;
        const ready = appStatusTotals.data?.org?.readyForInstall?.total ?? 0;
        const inReview = appStatusTotals.data?.org?.inReview?.total ?? 0;
        const requiresAttention = appStatusTotals.data?.org?.requiresAttention?.total ?? 0;
        const submitted = appStatusTotals.data?.org?.submitted?.total ?? 0;
        const cancelled = appStatusTotals?.data?.org?.cancelled?.total ?? 0;
        const archived = appStatusTotals?.data?.org?.archived?.total ?? 0;
        const incomplete = appStatusTotals?.data?.org?.incomplete?.total ?? 0;

        const unknown = total - live - ready - inReview - requiresAttention - submitted - cancelled - archived - incomplete;

        return {
            live,
            ready,
            requiresAttention,
            inReview,
            submitted,
            cancelled,
            archived,
            incomplete,
            apps: total,
            unknown
        }
    }, [
        appStatusTotals.data?.org?.allApps?.total,
        appStatusTotals.data?.org?.archived?.total,
        appStatusTotals.data?.org?.cancelled?.total,
        appStatusTotals.data?.org?.inReview?.total,
        appStatusTotals.data?.org?.incomplete?.total,
        appStatusTotals.data?.org?.live?.total,
        appStatusTotals.data?.org?.readyForInstall?.total,
        appStatusTotals.data?.org?.requiresAttention?.total,
        appStatusTotals.data?.org?.submitted?.total
    ]);

    const { fetching: tableLoading, data } = appsByStats;

    const appTableData: AppsTableData[] = !tableLoading && data ? getAppsTableData(data?.org?.apps?.apps) : [];
    const totalItems = !tableLoading && data ? data?.org?.apps?.total : 0;

    const totalApps = data?.org?.allApps?.total;
    const firstAppId = data?.org?.allApps?.apps?.[0]?.appId;

    function handleSelectStatus(status: AppStatus) {
        setSelectedStatus(status);
    }

    const handleSortClick = (orderValue: OrderByType) => {
        setOrderBy(orderValue)
    }

    const tableData: AppTableProps = {
        tableData: appTableData,
        tableLoading,
        pageSize,
        fromIndex: from,
        totalItems,
        selectedPage: currentPage,
        sortValue: orderBy,
        onSortClick: handleSortClick,
        onSelectPage: handleSelectPage,
        onPageSizeChange: handlePageSizeChange
    }

    const history = useHistory();

    const handleNotesValue = useCallback(
        (note: string, appId: string, status: string) => {
            updateApplicationStatus({
                appId,
                notes: note,
                type: status
            })
            // TODO: What are we doing this for?
            window.location.href = window.location.href
        }, [updateApplicationStatus])

    const handleUpdateAppStatus = useCallback(
        (status: string, appId: string) => {
            updateApplicationStatus({
                appId,
                type: status
            })

            const routeBase = `/${organizationId}/${appId}`;

            if (status.startsWith("Under Review")) {
                return history.push(`${routeBase}/channels`)
            }

        }, [history, organizationId, updateApplicationStatus])

    const handleActionClick = (status: string, appId: string) => {
        switch (status) {
            case "Live":
            case "Ready for Install":
            case "Requires Attention":
                break;
            case "Under Review":
                handleUpdateAppStatus("Ready for Install", appId);
                setSelectedStatus(undefined);
                break;
            case "Submitted For Review":
                handleUpdateAppStatus("Under Review", appId);
                setSelectedStatus(undefined);
                break;
            default:
                break;
        }
    }

    // If the org only has one app, just redirect to the app (unless viewing management page directly)
    if (!managementOnly && totalApps === 1 && firstAppId) {
        return <Redirect to={`/${organizationId}/${firstAppId}`} />;
    }

    return (
        <OrgOverviewView
            org={organization}
            user={user}
            activeStatus={selectedStatus}
            tableData={tableData}
            onCreateApp={handleCreateApp}
            onUploadTemplate={handleUploadTemplate}
            onSelectStatus={handleSelectStatus}
            onActionClick={handleActionClick}
            totals={totals}
            valueNotes={handleNotesValue}
            managementOnly={managementOnly}
        />
    );
};

/**
 * Standalone management page - renders only the fleet management section
 */
export const OrgManagementContainer: FC<Omit<OrgOverviewViewContainerProps, 'managementOnly'>> = (props) => {
    return <OrgOverviewViewContainer {...props} managementOnly />;
};
